/**
 * One card per idea: what it IS, and what surrounds it.
 *
 * The first cut listed pains, numbers and rivals and left the reader to work
 * out the product. Now the card opens with the ANGLE the strategist wrote,
 * says where the idea STANDS OUT, and lists the FEATURES it would need with
 * the pains each one solves. The standing of a feature — yours alone, shared,
 * table stakes — is read off the rivals' HAS_FEATURE links by the server, not
 * asserted by a persona: it is the same fact the feature matrix shows.
 */
import type { BoardroomIdeaBrief, BoardroomIdeaFeature, BoardroomOpportunityBrief } from '../../../types';
import { CARD } from './tableLayout';
import { VerdictBadge } from '../ItemCard';
import Critiques from './Critiques';
import MissingList, { type OnDig } from './MissingList';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';
import PromoteIdea from '../PromoteIdea';

const STANDING: Record<BoardroomIdeaFeature['standing'], { label: string; className: string }> = {
  ours: {
    label: 'yours alone',
    className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  shared: {
    label: 'some rivals have it',
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  table_stakes: {
    label: 'table stakes',
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
};

function standingLabel(f: BoardroomIdeaFeature, rivals: number): string {
  if (f.standing === 'ours') return STANDING.ours.label;
  if (f.standing === 'table_stakes') {
    return f.rivals.length === rivals ? `table stakes · all ${rivals} rivals` : `table stakes · ${f.rivals.length} of ${rivals} rivals`;
  }
  return `${f.rivals.join(', ')} ${f.rivals.length === 1 ? 'has' : 'have'} it`;
}

/**
 * The sentences a reader would otherwise have to assemble from three columns.
 * Each line is one fact off the board; nothing here is written by a persona.
 */
/** The named items in a claim, each clickable. The sharpest lines in the brief
 *  — "only you would ship this", "nobody covers that" — were the one part a
 *  reader could not check by clicking; everything around them linked. */
function Refs({
  refs,
  onOpen,
  suffix,
}: {
  refs: { id: number; title: string }[];
  onOpen: (itemId: number) => void;
  suffix?: (r: { id: number; title: string }) => string;
}) {
  return (
    <>
      {refs.map((r, i) => (
        <span key={r.id}>
          {i > 0 && ', '}
          <button
            type="button"
            onClick={() => onOpen(r.id)}
            className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            {r.title}
          </button>
          {suffix ? suffix(r) : ''}
        </span>
      ))}
    </>
  );
}

type StandsOutLine = {
  key: string;
  tone: 'good' | 'warn';
  before: string;
  refs?: { id: number; title: string }[];
  suffix?: (r: { id: number; title: string }) => string;
  after?: string;
};

function StandsOut({ idea, onOpen }: { idea: BoardroomIdeaBrief; onOpen: (itemId: number) => void }) {
  const so = idea.stands_out;
  const lines: StandsOutLine[] = [];
  if (so.only_ours.length) {
    lines.push({
      key: 'ours',
      before: 'Only this idea has ',
      refs: so.only_ours,
      after: `${so.rivals ? ` - none of ${so.rivals} rival${so.rivals === 1 ? '' : 's'} under this type ship it` : ''}.`,
      tone: 'good',
    });
  }
  if (so.gaps_we_fill.length) {
    lines.push({
      key: 'gaps',
      before: so.rivals ? `Fills a gap: none of ${so.rivals} rivals address ` : 'Answers ',
      refs: so.gaps_we_fill,
      after: so.rivals
        ? '.'
        : `, and no product on the board addresses ${so.gaps_we_fill.length === 1 ? 'it' : 'them'}.`,
      tone: 'good',
    });
  }
  if (so.table_stakes_missing.length) {
    lines.push({
      key: 'stakes',
      before: 'Would still need what most rivals already ship: ',
      refs: so.table_stakes_missing,
      suffix: t => ` (${(t as { rivals?: number }).rivals} of ${so.rivals} ship it)`,
      after: '.',
      tone: 'warn',
    });
  }
  // The landscape cell nobody else is in, if the cartographer has placed it.
  const alone = so.placement.filter(pl => pl.rivals_sharing === 0);
  const shared = so.placement.filter(pl => pl.rivals_sharing > 0);
  if (so.rivals && alone.length) {
    lines.push({
      key: 'place',
      before: `No rival is ${alone.map(pl => `${pl.dimension.toLowerCase()}: ${pl.value}`).join(' or ')}${
        shared.length ? `; shares ${shared.map(pl => `${pl.dimension.toLowerCase()}: ${pl.value}`).join(', ')} with ${shared.map(pl => pl.rivals_sharing).join('/')}` : ''
      }.`,
      tone: 'good',
    });
  } else if (shared.length) {
    lines.push({
      key: 'place',
      before: `Sits with ${shared.map(pl => `${pl.rivals_sharing} rival${pl.rivals_sharing === 1 ? '' : 's'} on ${pl.dimension.toLowerCase()}: ${pl.value}`).join(', ')}.`,
      tone: 'warn',
    });
  }
  // The price point against the band. Says "no rival publishes a price" rather
  // than nothing: the absence of a band is a finding about the field.
  if (idea.price) {
    const pp = idea.price_position;
    const money = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
    let text: string;
    let tone: 'good' | 'warn' = 'good';
    if (pp.band && pp.position) {
      const band = pp.band.from === pp.band.to ? `${money(pp.band.from)}/mo` : `${money(pp.band.from)}–${money(pp.band.to)}/mo`;
      const verb = pp.position === 'undercuts' ? 'undercuts' : pp.position === 'premium' ? 'is priced above' : 'sits within';
      text = `At ${idea.price.label} it ${verb} the rivals' ${band}`;
      if (pp.position === 'premium') tone = 'warn';
    } else if (pp.priced_rivals === 0) {
      text = `At ${idea.price.label}; no rival under this type publishes a price to compare`;
    } else {
      text = `At ${idea.price.label}; the rivals' prices are not on a monthly line`;
    }
    if (pp.unit_matches === false) {
      text += ` - priced ${idea.price.unit}, where the field prices ${pp.field_units.join(' / ')}`;
      tone = 'warn';
    }
    // THE NUMBER A BUYER QUESTIONS HARDEST is now the one you can click.
    lines.push({
      key: 'price',
      before: 'At ',
      refs: [{ id: idea.price.id, title: idea.price.label ?? 'this plan' }],
      after: `${text}.`,
      tone,
    });
  }
  if (so.demand && so.demand.volume !== null) {
    const g = so.demand.growth;
    const trend = g === null ? '' : g > 0.05 ? `, growing ${Math.round(g * 100)}%` : g < -0.05 ? `, shrinking ${Math.round(-g * 100)}%` : ', flat';
    lines.push({
      key: 'demand',
      before: `Why now: ${so.demand.volume.toLocaleString()} searches a month for ${so.demand.title}${trend}.`,
      tone: g !== null && g < -0.05 ? 'warn' : 'good',
    });
  }
  if (so.venues.length) {
    lines.push({ key: 'venues', before: `These buyers were found talking on ${so.venues.slice(0, 4).join(', ')}.`, tone: 'good' });
  }
  if (!lines.length) return null;
  return (
    <ul className="mt-2 flex flex-col gap-0.5 text-xs" data-testid={`stands-out-${idea.id}`}>
      {lines.map(l => (
        <li key={l.key} className={l.tone === 'good' ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}>
          {l.before}
          {l.refs && <Refs refs={l.refs} onOpen={onOpen} suffix={l.suffix} />}
          {l.after}
        </li>
      ))}
    </ul>
  );
}


/**
 * How good it is, on the same questions every time.
 *
 * POLARITY IS THE WHOLE POINT. "How good is what exists: 5" is bad news and
 * "pain severity: 5" is good news, and painting them the same colour tells the
 * reader the opposite of what the number means. A score resting on nothing says
 * so: an operator's judgement is worth having, and worth telling apart from a
 * number somebody can follow to a page.
 */
function Scores({ idea }: { idea: BoardroomIdeaBrief }) {
  const scores = idea.scores ?? [];
  if (!scores.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5 text-xs" data-testid={`scores-${idea.id}`}>
      {scores.map(s => {
        const good = s.inverted ? s.score <= 2 : s.score >= 4;
        const bad = s.inverted ? s.score >= 4 : s.score <= 2;
        const tone = good
          ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
          : bad
            ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
        return (
          <li
            key={s.key}
            className={`px-1.5 py-0.5 rounded ${tone}`}
            title={[s.why, s.grounded ? null : 'nothing on the board backs this'].filter(Boolean).join(' - ')}
          >
            {s.label} <span className="font-semibold tabular-nums">{s.score}</span>
            <span className="opacity-60">/5</span>
            {!s.grounded && <span className="ml-1 opacity-70">no receipt</span>}
          </li>
        );
      })}
    </ul>
  );
}

export default function OpportunityBriefReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
  boardroomId,
  canPublish,
}: {
  data: BoardroomOpportunityBrief;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  /** Remembers the marks across a refresh, per room. */
  roomName?: string;
  /** The room, so each card can offer the way out: the idea becomes a subject. */
  boardroomId?: number;
  canPublish?: boolean;
}) {
  const empty = data.totals.ideas === 0;
  /* THE NINE FIELDS OF AN IDEA. They used to exist only to feed the picker and
     a hand-written CSV shaper — a column list for a table nobody drew. They
     are real columns now: read down they are the cards, and flipped they are
     the table those cards could never be. */
  /* WIDTHS, BECAUSE THE FALLBACK IS AN EQUAL SHARE. A column that declares
     none is weighted 10 like every other, so `pains nobody addresses` — two
     digits — took exactly as much of the screen as the idea's title, and the
     four slash-joined lists that carry the actual reading were cut off to pay
     for it. Ratios, not pixels: what matters is which columns deserve more. */
  const ideaColumns: Column<BoardroomIdeaBrief>[] = [
    { key: 'idea', label: 'idea', always: true, flipLabel: 'field', width: 22, value: i => i.title,
      cell: i => (
        <button type="button" onClick={() => onOpen(i.id)} className="font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left">
          {i.title}
        </button>
      ) },
    { key: 'type', label: 'solution type', width: 12, value: i => i.solution_type?.title ?? '' },
    { key: 'verdict', label: 'verdict', width: 7, value: i => i.verdict ?? '',
      cell: i => (i.verdict ? <VerdictBadge verdict={i.verdict} /> : '') },
    { key: 'angle', label: 'angle', width: 16, value: i => i.angle ?? '' },
    { key: 'pains', label: 'pains', width: 18, value: i => i.pains.map(p => p.title).join(' / ') },
    { key: 'unaddressed', label: 'pains nobody addresses', align: 'right', width: 6, value: i => i.unaddressed_pains },
    { key: 'demand', label: 'demand', width: 14,
      value: i => i.demand.map(d => `${d.title}${d.volume ? ` (${d.volume})` : ''}`).join(' / ') },
    { key: 'price', label: 'price', width: 8, value: i => i.price?.label ?? '' },
    { key: 'features', label: 'features', width: 18, value: i => i.features.map(f => f.title).join(' / ') },
    { key: 'assumptions', label: 'assumptions', width: 18, value: i => i.assumptions.map(a => a.title).join(' / ') },
    { key: 'critiques', label: 'critiques', align: 'right', width: 6, value: i => i.critiques },
  ];
  // AN IDEA IS A CARD, NOT A ROW, and what changes about it is the positioning
  // that lands on it — an angle written, a price set, a pain linked. The card
  // stays marked until it has been on screen, which for a card the height of
  // this one means genuinely read.
  const seen = useReportSeen(unseen, onSeen, [
    ...(data.ideas ?? []).map(i => i.id),
    ...data.missing.map(m => m.item_id),
  ]);

  const cards = (
    <>
      {data.ideas.map(idea => (
        <article
          key={idea.id}
          ref={seen.mark(idea.id)}
          className={`${CARD} ${seen.cls(
            idea.id,
          )}`}
          data-testid={`idea-${idea.id}`}
        >
          <div className="flex items-baseline gap-2 flex-wrap">
            <button type="button" onClick={() => onOpen(idea.id)} className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left">
              {idea.title}
            </button>
            {idea.verdict && <VerdictBadge verdict={idea.verdict} />}
            {idea.solution_type ? (
              <button type="button" onClick={() => onOpen(idea.solution_type!.id)} className="text-xs text-violet-700 dark:text-violet-300 hover:underline">
                {idea.solution_type.title}
              </button>
            ) : (
              <span className="text-xs text-gray-400 italic">no solution type yet</span>
            )}
            <Critiques rows={idea.critique_rows} onOpen={onOpen} />
          </div>

          {/* THE ANGLE. The strategist's words, verbatim: who it is for, the
              wedge, why they would switch. Absent, the card says so rather than
              leaving a gap the reader might take for "nothing to say". */}
          {idea.angle ? (
            <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-200 whitespace-pre-line">{idea.angle}</p>
          ) : (
            <p className="mt-2 text-xs text-gray-400 italic">not positioned yet - no angle written</p>
          )}
          {(idea.marketing_angles ?? []).length > 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid={`leads-with-${idea.id}`}>
              Leads with: {(idea.marketing_angles ?? []).map(a => a.replace(/_/g, ' ')).join(', ')}
            </p>
          )}
          <StandsOut idea={idea} onOpen={onOpen} />
          <Scores idea={idea} />
          {/* WHAT HAS TO BE TRUE. The angle's claims as questions, with a mark
              for the ones the board has answered. An angle with no assumptions
              is a pitch; this is what makes it a plan. */}
          {idea.assumptions.length > 0 && (
            <ul className="mt-2 flex flex-col gap-0.5 text-xs" data-testid={`assumptions-${idea.id}`}>
              <li className="text-[11px] uppercase tracking-wide text-gray-400">
                Assumptions to test · {idea.assumptions.filter(a => a.answered).length} of {idea.assumptions.length} answered
              </li>
              {idea.assumptions.map(a => (
                <li key={a.id} className="flex items-baseline gap-1.5">
                  <span className={a.answered ? 'text-emerald-500' : 'text-gray-400'} aria-label={a.answered ? 'answered' : 'open'}>
                    {a.answered ? '✓' : '○'}
                  </span>
                  <button type="button" onClick={() => onOpen(a.id)} className="text-gray-700 dark:text-gray-200 hover:underline text-left">
                    {a.title}
                  </button>
                  {a.answer && (
                    <button type="button" onClick={() => onOpen(a.answer!.id)} className="text-[11px] text-gray-400 hover:underline text-left">
 - {a.answer.title}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
            <div>
              <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Features it would need</h4>
              {idea.features.length === 0 && <span className="text-gray-400 italic">none planned yet</span>}
              <ul className="flex flex-col gap-1.5">
                {idea.features.map(f => (
                  <li key={f.id} data-testid={`feature-${f.id}`}>
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <button type="button" onClick={() => onOpen(f.id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
                        {f.title}
                      </button>
                      <span className={`text-[11px] px-1.5 rounded-full whitespace-nowrap ${STANDING[f.standing].className}`} title={f.rivals.length ? `shipped by ${f.rivals.join(', ')}` : 'no rival under this type ships it'}>
                        {standingLabel(f, idea.stands_out.rivals)}
                      </span>
                    </div>
                    {f.body && <div className="text-xs text-gray-500 dark:text-gray-400">{f.body}</div>}
                    <div className="text-[11px] text-gray-400">
                      {f.pains.length
                        ? `for ${f.pains.map(p => p.title).join(', ')}`
                        : 'not tied to a pain'}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Pains it answers</h4>
              {idea.pains.length === 0 && <span className="text-gray-400 italic">none linked yet</span>}
              <ul className="flex flex-col gap-1">
                {idea.pains.map(p => (
                  <li key={p.id}>
                    <div className="flex items-baseline gap-1.5">
                      <button type="button" onClick={() => onOpen(p.id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
                        {p.title}
                      </button>
                      <span className="text-[11px] text-gray-400 whitespace-nowrap">
                        {p.evidence} evidence{p.addressed_by.length ? ` · also ${p.addressed_by.join(', ')}` : ' · unaddressed'}
                      </span>
                    </div>
                    {/* What of OURS answers it. "nothing planned" is the hole
                        the plan dig fills; a feature name is the plan. */}
                    <div className={`text-[11px] ${p.our_features.length ? 'text-gray-500 dark:text-gray-400' : 'text-amber-700 dark:text-amber-300'}`}>
                      {p.our_features.length ? `→ ${p.our_features.join(', ')}` : 'nothing planned for it'}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Demand</h4>
              {idea.demand.length === 0 && (
                <span className="text-gray-400 italic">{idea.has_demand_lookup ? 'looked up, nothing recorded yet' : 'no numbers yet'}</span>
              )}
              <ul className="flex flex-col gap-1">
                {idea.demand.map(d => (
                  <li key={d.evidence_id}>
                    <button type="button" onClick={() => onOpen(d.evidence_id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
                      {d.title}
                    </button>
                    {(d.volume !== null || d.growth !== null) && (
                      <span className="ml-1 text-[11px] font-mono text-gray-500">
                        {d.volume !== null && `${d.volume.toLocaleString()}/mo`}
                        {d.growth !== null && ` ${d.growth > 0 ? '+' : ''}${Math.round(d.growth * 100)}%`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Nearest competitors</h4>
              {idea.competitors.length === 0 && <span className="text-gray-400 italic">none filed under this type</span>}
              <ul className="flex flex-col gap-1">
                {idea.competitors.slice(0, 8).map(c => (
                  <li key={c.product_id} className="flex items-baseline gap-1.5">
                    <button type="button" onClick={() => onOpen(c.product_id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
                      {c.title}
                      {c.verdict === 'HOLDS' && <span className="text-emerald-500"> ✓</span>}
                    </button>
                    <span className="text-[11px] text-orange-700 dark:text-orange-300 whitespace-nowrap">
                      {c.cheapest ? `from ${c.cheapest.label}` : 'no pricing yet'}
                    </span>
                  </li>
                ))}
                {idea.competitors.length > 8 && <li className="text-[11px] text-gray-400">and {idea.competitors.length - 8} more</li>}
              </ul>
            </div>
          </div>

          {/* THE WAY OUT, on the card that argues for it: the idea becomes a
              subject, with this brief as its description and the terms the room
              pulled numbers for as its keywords. */}
          {boardroomId != null && (
            <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-2">
              <PromoteIdea
                boardroomId={boardroomId}
                ideaId={idea.id}
                promotedProjectId={idea.promoted_project_id ?? null}
                canPublish={!!canPublish}
                compact
              />
            </div>
          )}
        </article>
      ))}
    </>
  );

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {data.totals.ideas} idea{data.totals.ideas === 1 ? '' : 's'} · {data.totals.positioned} positioned · {data.totals.priced} priced · {data.totals.with_pains} with a pain behind them · {data.totals.with_demand} with demand numbers
        </div>
      )}
      {/* AN IDEA DOES NOT FLATTEN INTO A ROW. Each is a paragraph, a list of
          sentences and four lists of its own, so a cell per field would be a
          worse drawing of the card it replaced. Read down, it stays a card.
          FLIPPED, it is the table those cards could never be: an idea per
          column, the nine fields down the side, ideas read against each other
          rather than one after another. Same strip either way, and the picker
          still says what the file carries. */}
      {!empty && (
        <DataTable
          table="opportunities"
          room={roomName}
          columns={ideaColumns}
          rows={data.ideas}
          rowKey={i => `idea-${i.id}`}
          unflipped={cards}
          // Reading forty ideas against each other is still the reading you
          // came for, and flipping is the only way to this table at all.
          flipMax={200}
          flippedMode="long"
        />
      )}
      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
